import { Alert, Paper } from '@mui/material';
import Grid from '@mui/material/Grid';

import { useAuth } from '../../zeon-react-ui/auth/model';
import { grey } from '@mui/material/colors';
import { NoBackpackSharp } from '@mui/icons-material';

interface IIndexable {
  [key: string]: any;
}

interface IDMPayloadVariantDetail extends IIndexable {
  so_supply: number,
  so_request: number,
  po_requisition: number,
  po_supply: number,
  mrp_demand: number,
  co_demand: number,
  so_request_demand: number,
  po_request_demand: number
}

interface IDMPayloadVariantDetailBlock {
  [deliveryDate: string]: IDMPayloadVariantDetail
}

interface IDMPayloadVariantTotals extends IIndexable {
  on_hand: number,
  reserved: number,
  difference: number,
  so_supply: number,
  so_request: number,
  po_requisition: number,
  po_supply: number,
  mrp_demand: number,
  co_demand: number,
  so_request_demand: number,
  po_request_demand: number,
  available: number,
  actual: number
}

interface IDMPayloadDataVariant {
  totals: IDMPayloadVariantTotals,
  detail: IDMPayloadVariantDetailBlock
}

interface IDMPayloadDataVariantBlock {
  [size: string]: IDMPayloadDataVariant
}

interface IDMPayloadDataRow {
  id: number,
  commodity: string,
  sku: string,
  collection: string,
  description: string,
  uom: string,
  variants: IDMPayloadDataVariantBlock,
}

interface IDMSkuItemProps {
  data: IDMPayloadDataRow
}

interface IDMVariantItemProps {
  size: string;
  data: IDMPayloadDataVariant;
}

let itemKeyCounter = 0;

const VariantItemTotals = (props: IDMVariantItemProps) => {
  const auth = useAuth();
  const mask = auth["appdata"]["ifs"]["dismag"]["quantity"] || false;
  const actualValue = props.data.totals.on_hand - props.data.totals.co_demand;
  const rawValue = actualValue >= 0 ? actualValue : 0;
  const maskedValue: string = mask ? (rawValue >= 10 ? ">10" : `${rawValue}`) : `${rawValue}`;
  const value = (!mask && rawValue > 1000 ? `${Math.round(rawValue / 1000)}k` : `${maskedValue}`);
  return <Grid key={`item-totals-${itemKeyCounter++}`} item={true} container sx={{ p: '4px', minWidth: 60 }}>
    {props.size !== "@NO:VARS@" ? <Grid key={`item-totals-${itemKeyCounter}-size`} item={true} xs={12} sx={{ textAlign: "center" }}><b>{props.size}</b></Grid> : null}
    <Grid key={`item-totals-${itemKeyCounter}-title`} item={true} xs={12} sx={{ textAlign: "center" }}>{rawValue ? value : "-"}</Grid>
    <VariantItemDetail size={props.size} data={props.data} />
  </Grid>;
}

const VariantItemDetail = (props: IDMVariantItemProps) => {
  const auth = useAuth();
  const mask = auth["appdata"]["ifs"]["dismag"]["quantity"] || false;
  let avail = props.data.totals.on_hand - props.data.totals.co_demand;
  let payback = 0;
  if (avail<0) {
    payback = avail;
    avail = 0;
  }
  const delivery = props.data.detail ? Object.keys(props.data.detail).reduce((result: string[], vDate) => {
    const one = props.data.detail[vDate];
    if ((result.indexOf(vDate) === -1) && (one.so_supply + one.po_supply > 0)) result.push(vDate);
    return result;
  }, []) : [];
  return (
    <Grid key={`item-totals-${itemKeyCounter++}-var`} item={true} container sx={{ p: 0 }}>
      {delivery.map(dDate => {
        const current = props.data.detail && props.data.detail[dDate] ? props.data.detail[dDate] : null;
        let vt = (current?.po_supply || 0) + (current?.so_supply || 0);
        let vt1 = vt + payback;
        if (vt1 >= 0) payback = 0;
        if (vt1 < 0) {
          payback += vt;
          vt1 = 0;
        }
        vt = vt1;
        const maskedValue: string = mask ? (vt >= 10 ? ">10" : `${vt}`) : `${vt}`;
        const value = (!mask && vt > 1000 ? `${Math.round(vt / 1000)}k` : `${maskedValue}`);
        const dateParts = dDate.split("-");
        const lDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
        return (vt ?
          <Grid key={`item-totals-${itemKeyCounter++}-varct`} item={true} container >
            <Grid key={`item-totals-${itemKeyCounter}-label`} item={true} xs={9} sx={{ pt: 0, textAlign: 'left' }}><small style={{ fontSize: 9 }}><b>{lDate}</b></small></Grid>
            <Grid key={`item-totals-${itemKeyCounter}-value`} item={true} xs={3} sx={{ pt: 0, textAlign: 'right' }}><small style={{ fontSize: 9 }}><b>{value}</b></small></Grid>
          </Grid> :
          null
        );
      })}
    </Grid>
  );
}

interface IPaybackList {
  [index: string]: number;
}
const VariantItemGrid = (props: IDMSkuItemProps) => {
  const auth = useAuth();
  const mask = auth["appdata"]["ifs"]["dismag"]["quantity"] || false;
  const { id } = props.data;
  const vars = props.data.variants;
  const dates: string[] = [];
  const filtered: string[] = [];
  const payback: IPaybackList = {};
  const sizes: string[] = Object.keys(vars);

  sizes.forEach((size: string) => {
    Object.keys(vars[size].detail).forEach((when: string) => {
      if (dates.indexOf(when) === -1) dates.push(when);
    });
  });
  dates.sort().forEach((when: string) => {
    const qty: number = sizes.reduce((r: number, size: string): number => {
      const vd = vars[size] && (vars[size].detail[when] !== undefined) ? vars[size].detail[when] : null;
      if (vd && (vd.po_supply + vd.so_supply > 0)) r += vd.po_supply + vd.so_supply
      return r;
    }, 0);
    if (qty) filtered.push(when);
  });

  /** VARIANTS */
  return <Grid key={`item-grid-container-${id}`} item={true} container xs={12}>
    {/* Grid Title with variants */}
    {((sizes.length === 1) && (sizes[0] == "@NO:VARS@")) ? null :
      <Grid key={`item-grid-container-${id}-headrow`} item={true} container xs={12}>
        <Grid key={`item-grid-container-${id}-headrow-date`} item={true} xs={2} sx={{ borderBottom: "1px solid #ccc" }}>&nbsp;</Grid>
        {sizes.map((size: string, index: number) => {
          return <Grid key={`item-grid-container-${id}-headrow-size-${index}`} item={true} xs={1} sx={{ textAlign: 'center', borderBottom: "1px solid #ccc" }}><b>{size}</b></Grid>
        })}
      </Grid>
    }
    {/* Grid variants avaiability */}
    <Grid key={`item-grid-container-${id}-avail`} item={true} container xs={12}>
      <Grid key={`item-grid-container-${id}-headrow-avail`} item={true} xs={2} sx={{ borderBottom: "1px solid #ccc" }}><b>Available</b></Grid>
      {sizes.map((size: string, index: number) => {
        const vd = vars[size] ? vars[size].totals : null;
        let vt = vd ? vd.on_hand - vd.co_demand : 0
        if (vt < 0) {
          payback[size] = vt;
          vt = 0;
        } else {
          payback[size] = 0;
        }
        const out = vt ? (mask ? (vt >= 10 ? ">10" : vt) : vt) : "-"
        return <Grid key={`item-grid-container-${id}-headrow-size-${index}`} item={true} xs={1} sx={{ textAlign: 'center', borderBottom: "1px solid #ccc" }}>{out}</Grid>
      })}
    </Grid>
    {/* Grid Date Details */}
    {
      filtered.map((when: string, index: number) => {
        const rowkey = `item-grid-container-${id}-row-${when}`;
        const wop = when.split("-");
        const wo = `${wop[2]}-${wop[1]}-${wop[0]}`;
        return <Grid key={rowkey} item={true} container xs={12}>
          <Grid key={`${rowkey}-date`} item={true} xs={2} sx={{ borderBottom: "1px solid #ccc" }}><b>{wo}</b></Grid>
          {
            sizes.map((size: string, index: number) => {
              const vd = vars[size] && (vars[size].detail[when] !== undefined) ? vars[size].detail[when] : null;
              let vt = vd ? vd.so_supply + vd.po_supply : 0;
              if (vt > 0) {
                let vt1 = vt + payback[size];
                if (vt1 >= 0) payback[size] = 0;
                if (vt1 < 0) {
                  payback[size] += vt;
                  vt1 = 0;
                }
                vt = vt1;
              }
              const out = vt ? (mask ? (vt >= 10 ? ">10" : vt) : vt) : "-";
              return <Grid key={`${rowkey}-${size}-${index}`} item={true} xs={1} sx={{ textAlign: 'center', borderBottom: "1px solid #ccc" }}>{out}</Grid>
            })
          }
        </Grid>
      })
    }
  </Grid>;
}

const SkuItem = (props: IDMSkuItemProps) => {
  return <Paper sx={{ mb: "8px", p: 1, width: "100%" }}><Grid key={`item-${itemKeyCounter++}-ct`} item={true} container>
    <Grid key={`item-${itemKeyCounter}-sku`} item={true} xs={4} md={3} sx={{ pr: 1 }}><small style={{ fontSize: 8 }}><i>SKU</i>:</small><br /><b>{props.data.sku}</b></Grid>
    <Grid key={`item-${itemKeyCounter}-desc`} item={true} xs={8} md={4}><small style={{ fontSize: 8 }}><i>Description</i>:</small><br />{props.data.description}</Grid>
    <Grid key={`item-${itemKeyCounter}-comm`} item={true} xs={2} md={2} sx={{ pr: 1 }}><small style={{ fontSize: 8 }}><i>Commodity</i>:</small><br />{props.data.commodity}</Grid>
    <Grid key={`item-${itemKeyCounter}-uom`} item={true} xs={2} md={1} sx={{ pr: 1 }}><small style={{ fontSize: 8 }}><i>UoM</i>:</small><br />{props.data.uom}</Grid>
    <Grid key={`item-${itemKeyCounter}-age`} item={true} xs={2} md={2} sx={{ pr: 1 }}><small style={{ fontSize: 8 }}><i>Collection</i>:</small><br />{props.data.collection}</Grid>
    <Grid key={`item-${itemKeyCounter}-var`} item={true} xs={12} container sx={{ justifyContent: 'left' }}>
      {Object.keys(props.data.variants).map((key, idx) => {
        return (
          <Grid key={`item-${props.data.id}-${idx}-detailctxs`} item={true} xs={3} lg={1} sx={{ mt: "6px", justifyContent: 'left', display: { xs: 'block', sm: 'none' } }}>
            <div style={{ border: '1px solid #ccc', borderRadius: '5px', marginLeft: "2px", marginRight: "2px", height: "100%" }}>
              {<VariantItemTotals key={`variant-item-total-${props.data.id}-${idx}`} size={key} data={props.data.variants[key]} />}
            </div>
          </Grid>
        )
      })}
      <Grid key={`item-${props.data.id}-detailctmd`} item={true} xs={12} sx={{ mt: "6px", justifyContent: 'left', display: { xs: 'none', sm: 'block' } }}>
        <div style={{ marginLeft: "2px", marginRight: "2px", paddingLeft: "6px", paddingBottom: "6px" }}>
          {<VariantItemGrid key={`variant-item-grid-${props.data.id}`} data={props.data} />}
        </div>
      </Grid>
    </Grid>
  </Grid></Paper>;
}

interface IDMDGBackAction {
  (): void;
}
interface IDMDisplayGridProps {
  data: {
    params: {
      site: string,
      location: string,
      collection: string,
      commodity: string,
      timing: boolean,
      quantity: boolean,
      sku: string
    },
    data: IDMPayloadDataRow[]
    ifs_time: number;
    njs_time: number;
  };
  onBackToParams?: IDMDGBackAction;
}

export const DMDisplayGrid = (props: IDMDisplayGridProps) => {
  const { data } = props.data;
  return <div>
    <Grid item={true} xs={12} container style={{ justifyContent: 'center' }}>
      {data.length == 0 ?
        <Alert severity="warning">No data to display with given parameters!</Alert> :
        data.map((row, idx) => {
          return (
            <SkuItem key={`sku-item-${idx}`} data={row} />
          )
        })}
    </Grid>
  </div>
}
